
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Scrolls smoothly to the element with the given ID.
   * If the current route is not the home page ("/"), it first navigates
   * to the home page, waits for the DOM to render, then performs the scroll.
   *
   * @param sectionId - The id attribute of the target element.
   */
  const scrollToSection = (sectionId: string) => {
    const performScroll = () => {
      const element = document.getElementById(sectionId);
      if (!element) {
        console.warn(`Element with id "${sectionId}" not found.`);
        return;
      }
      element.scrollIntoView({ behavior: 'smooth' });
    };

    if (location.pathname !== '/') {
      // Use React Router's navigate instead of a non‑existent global function.
      navigate('/', { replace: false });
      // Give the new page a short moment to mount before trying to scroll.
      setTimeout(performScroll, 100);
    } else {
      performScroll();
    }

    // Close mobile menu after a navigation attempt.
    setIsMenuOpen(false);
  };

  const goToDirectorProfile = () => {
    navigate('/director-profile');
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* ロゴと社名 */}
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/b90f078cf90066d31dc5715f1c64017a/36c76535dddd88638941c6c99da87bd5.png"
              alt="Y's STUDIO Logo"
              className="w-14 h-14 object-contain"
            />
            <div>
              <h1 className="text-xl font-medium text-navy">Y&apos;s STUDIO</h1>
              <p className="text-xs text-navy-light">KOBE JAPAN</p>
            </div>
          </div>

          {/* デスクトップナビゲーション */}
          <nav className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-gray-700 hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
            >
              ホーム
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
            >
              戦略設計
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="text-gray-700 hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
            >
              制作事例
            </button>
            <button
              onClick={() => scrollToSection('our-thoughts')}
              className="text-gray-700 hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
            >
              戦略設計責任者について
            </button>
            <button
              onClick={goToDirectorProfile}
              className="text-gray-700 hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
            >
              技術責任者について
            </button>
          </nav>

          {/* モバイルメニューボタン */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-navy transition-colors cursor-pointer"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* モバイルナビゲーション */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <nav className="py-4 space-y-2">
              <button
                onClick={() => scrollToSection('hero')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:text-navy hover:bg-accent-light transition-colors cursor-pointer"
              >
                ホーム
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:text-navy hover:bg-accent-light transition-colors cursor-pointer"
              >
                戦略設計
              </button>
              <button
                onClick={() => scrollToSection('portfolio')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:text-navy hover:bg-accent-light transition-colors cursor-pointer"
              >
                制作事例
              </button>
              <button
                onClick={() => scrollToSection('our-thoughts')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:text-navy hover:bg-accent-light transition-colors cursor-pointer"
              >
                戦略設計責任者について
              </button>
              <button
                onClick={goToDirectorProfile}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:text-navy hover:bg-accent-light transition-colors cursor-pointer"
              >
                技術責任者について
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
